/**
 * Showing a deck's pages one after another for a tool that reads them (a
 * capture, a fit check), then leaving the deck as it was.
 */

import type { SceneObject } from '../../core/SceneObject';
import { Pages } from '../../behaviors/pages';
import { Properties } from '../../behaviors/properties';

/**
 * Call `run` with `show(index)` (0-based) switching the deck's page, then leave
 * the deck as it was: the page that showed (also under the all-pages overview,
 * where play and save start from it) and every group's state and editor pick.
 * Showing a page applies its states, and showing the old one again would apply
 * that page's states over a deck that was at Base. An editor pick (set_group_state's
 * "Base", say) shows over what a page assigns, so the picks are handed back to
 * the pages while `run` runs, then picked again, whatever `run` picked meanwhile.
 * The same page switch the editor's page cards and the `showPage` action make,
 * so it needs no play mode.
 */
export async function holdingDeck<T>(deck: SceneObject, run: (show: (index: number) => void) => Promise<T>): Promise<T> {
    const pages = deck.getBehavior(Pages)!;
    const showingAll = pages.isShowingAll();
    const shown = pages.getActivePageIndex();
    const groups = [deck, ...deck.getDescendants()].flatMap(object => {
        const props = object.getBehavior(Properties);
        return props ? [{ props, pick: props.getEditorState(), state: props.getRuntimeState() }] : [];
    });
    try {
        for (const { props, pick } of groups) if (pick !== undefined) props.setEditorState(undefined, true);
        return await run(index => pages.showPage(index, false));
    } finally {
        if (!deck.transform.isDisposed()) {
            pages.showPage(shown, false);
            if (showingAll) pages.showAllPages();
            for (const { props, pick, state } of groups) {
                // A state removed meanwhile can't come back: the group shows the page's.
                try { if (props.getRuntimeState() !== state) props.setState(state, true); } catch { /* removed */ }
                try { if (props.getEditorState() !== pick) props.setEditorState(pick, true); } catch { /* removed */ }
            }
        }
    }
}
